'use client'

import { useParams } from 'next/navigation'
import Link from 'next/link'
import { divingPools } from '@/data/divingPools'
import { useTheme } from '@/contexts/ThemeContext'

export default function PoolDetailPage() {
  const params = useParams()
  const { theme } = useTheme()
  const pool = divingPools.find(p => p.id === params.id)

  if (!pool) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        theme === 'light' ? 'bg-white' : 'bg-gray-900'
      }`}>
        <div className="text-center">
          <h1 className={`text-xl xxs:text-lg font-bold mb-3 xxs:mb-2 ${
            theme === 'light' ? 'text-gray-900' : 'text-white'
          }`}>풀장을 찾을 수 없습니다</h1>
          <Link href="/pools" className={`text-sm xxs:text-xs hover:underline ${
            theme === 'light' ? 'text-blue-600' : 'text-blue-400'
          }`}>
            전체 풀장 목록으로 돌아가기
          </Link>
        </div>
      </div>
    )
  }

  const levelText = pool.level === 'beginner' ? '초급 (5-7m)' : pool.level === 'intermediate' ? '중급 (10-15m)' : '상급 (20m+)'

  return (
    <div className={`min-h-screen py-6 xxs:py-3 px-4 xxs:px-2 ${
      theme === 'light'
        ? 'bg-gradient-to-b from-blue-50 to-white'
        : 'bg-gradient-to-b from-gray-900 to-gray-800'
    }`}>
      <div className="max-w-4xl mx-auto">
        <Link href="/pools" className={`hover:underline mb-3 xxs:mb-2 text-sm xxs:text-xs inline-block ${
          theme === 'light' ? 'text-blue-600' : 'text-blue-400'
        }`}>
          ← 전체 풀장 목록
        </Link>

        <div className={`rounded-lg shadow-xl p-6 xxs:p-3 ${
          theme === 'light' ? 'bg-white' : 'bg-gray-800'
        }`}>
          <h1 className={`text-2xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${
            theme === 'light' ? 'text-gray-900' : 'text-white'
          }`}>{pool.name}</h1>

          <div className="space-y-3 xxs:space-y-2 text-sm xxs:text-xs">
            <div className="flex items-start">
              <span className="text-lg xxs:text-base mr-2 xxs:mr-1.5">📍</span>
              <div>
                <h3 className={`font-semibold text-sm xxs:text-xs ${
                  theme === 'light' ? 'text-gray-900' : 'text-white'
                }`}>주소</h3>
                <p className={`text-xs xxs:text-[10px] ${
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                }`}>{pool.address}</p>
              </div>
            </div>

            <div className="flex items-start">
              <span className="text-lg xxs:text-base mr-2 xxs:mr-1.5">🏊</span>
              <div>
                <h3 className={`font-semibold text-sm xxs:text-xs ${
                  theme === 'light' ? 'text-gray-900' : 'text-white'
                }`}>수심</h3>
                <p className={`text-xs xxs:text-[10px] ${
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                }`}>{pool.depth}</p>
              </div>
            </div>

            <div className="flex items-start">
              <span className="text-lg xxs:text-base mr-2 xxs:mr-1.5">⭐</span>
              <div>
                <h3 className={`font-semibold text-sm xxs:text-xs ${
                  theme === 'light' ? 'text-gray-900' : 'text-white'
                }`}>추천 레벨</h3>
                <p className={`text-xs xxs:text-[10px] ${
                  theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                }`}>{levelText}</p>
              </div>
            </div>

            {pool.features && pool.features.length > 0 && (
              <div className="flex items-start">
                <span className="text-lg xxs:text-base mr-2 xxs:mr-1.5">✨</span>
                <div>
                  <h3 className={`font-semibold text-sm xxs:text-xs mb-1.5 xxs:mb-1 ${
                    theme === 'light' ? 'text-gray-900' : 'text-white'
                  }`}>특징</h3>
                  <div className="flex flex-wrap gap-1.5 xxs:gap-1">
                    {pool.features.map((feature, idx) => (
                      <span key={idx} className={`px-2 xxs:px-1.5 py-0.5 text-[10px] xxs:text-[9px] rounded-full ${
                        theme === 'light'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-blue-900 text-blue-200'
                      }`}>
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={`mt-6 xxs:mt-4 p-4 xxs:p-3 rounded-lg ${
            theme === 'light' ? 'bg-blue-50' : 'bg-blue-900/20'
          }`}>
            <h3 className={`font-bold text-base xxs:text-sm mb-2 xxs:mb-1.5 ${
              theme === 'light' ? 'text-gray-900' : 'text-white'
            }`}>이 풀장이 궁금하신가요?</h3>
            <p className={`text-xs xxs:text-[10px] mb-3 xxs:mb-2 ${
              theme === 'light' ? 'text-gray-600' : 'text-gray-400'
            }`}>
              오늘다이빙에서 {pool.name}을 이용하는 버디를 찾아보세요!
            </p>
            <Link
              href="/matching/new"
              className="inline-block px-4 xxs:px-3 py-2 xxs:py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs xxs:text-[10px]"
            >
              버디 매칭 신청하기
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
